// ─── Site-wide constants ────────────────────────────────────────
export const SITE_NAME = "LoadLogo";
export const DEFAULT_DESCRIPTION =
  "The comprehensive logo API. Fetch logos for any company, stock ticker, or cryptocurrency. Brand profiles, social links, colors, and AI-powered style variants.";

export const API_BASE = "https://api.loadlogo.com";
export const IMG_BASE = "https://img.loadlogo.com";

export const CACHE_DURATIONS = {
  short: "public, max-age=3600",          // 1 hour
  medium: "public, max-age=86400",         // 1 day
  long: "public, max-age=604800",          // 1 week
} as const;

// ─── Logo URL builder ───────────────────────────────────────────
export function buildLogoUrl(
  path: string,
  opts?: { size?: number; format?: string; style?: string; theme?: string },
): string {
  const params = new URLSearchParams();
  if (opts?.size) params.set("size", String(opts.size));
  if (opts?.format) params.set("format", opts.format);
  if (opts?.style) params.set("style", opts.style);
  if (opts?.theme) params.set("theme", opts.theme);
  const qs = params.toString();
  return `${IMG_BASE}/${path}${qs ? "?" + qs : ""}`;
}

// ─── Brand / domain lists ───────────────────────────────────────
export const MARQUEE_BRANDS = [
  "google.com", "apple.com", "spotify.com", "github.com", "stripe.com",
  "netflix.com", "slack.com", "figma.com", "notion.so", "vercel.com",
  "discord.com", "twitch.tv", "airbnb.com", "uber.com", "shopify.com",
  "dropbox.com", "zoom.us", "linkedin.com", "twitter.com", "reddit.com",
  "amazon.com", "meta.com", "microsoft.com", "tesla.com", "nvidia.com",
  "adobe.com", "paypal.com", "salesforce.com", "oracle.com", "ibm.com",
];

/** Superset of MARQUEE_BRANDS used in sitemaps and SEO. */
export const POPULAR_BRANDS = [
  "google.com", "apple.com", "spotify.com", "github.com", "stripe.com",
  "netflix.com", "slack.com", "figma.com", "notion.so", "vercel.com",
  "discord.com", "twitch.tv", "airbnb.com", "uber.com", "shopify.com",
  "dropbox.com", "zoom.us", "linkedin.com", "twitter.com", "reddit.com",
  "amazon.com", "meta.com", "microsoft.com", "tesla.com", "nvidia.com",
  "adobe.com", "paypal.com", "salesforce.com", "oracle.com", "ibm.com",
  "coinbase.com", "robinhood.com", "square.com", "plaid.com", "wise.com",
  "revolut.com", "binance.com", "kraken.com", "opensea.io", "uniswap.org",
  "atlassian.com", "datadog.com", "cloudflare.com", "mongodb.com", "snowflake.com",
  "hashicorp.com", "elastic.co", "grafana.com", "supabase.com", "planetscale.com",
];

// ─── Stock tickers ──────────────────────────────────────────────
export const POPULAR_TICKERS = [
  "AAPL", "MSFT", "GOOGL", "AMZN", "TSLA", "META", "NVDA", "JPM", "V", "WMT",
];

// ─── Crypto ─────────────────────────────────────────────────────
export const POPULAR_CRYPTO = [
  "btc", "eth", "sol", "ada", "doge", "xrp", "dot", "avax", "matic", "link",
];

// ─── Blockchain chains ──────────────────────────────────────────
export const POPULAR_CHAINS = [
  { id: "1", name: "Ethereum" },
  { id: "8453", name: "Base" },
  { id: "137", name: "Polygon" },
  { id: "42161", name: "Arbitrum" },
  { id: "10", name: "Optimism" },
  { id: "56", name: "BNB Chain" },
  { id: "43114", name: "Avalanche" },
  { id: "250", name: "Fantom" },
  { id: "324", name: "zkSync Era" },
  { id: "100", name: "Gnosis" },
  { id: "1101", name: "Polygon zkEVM" },
  { id: "534352", name: "Scroll" },
] as const;

/** Chain IDs used for sitemap generation. */
export const POPULAR_CHAINS_SITEMAP = [
  "1", "8453", "137", "42161", "10", "56", "43114", "250", "324", "100",
];

// ─── ENS ────────────────────────────────────────────────────────
export const POPULAR_ENS = [
  "vitalik.eth",
  "nick.eth",
  "brantly.eth",
  "coinbase.eth",
  "opensea.eth",
  "uniswap.eth",
  "aave.eth",
  "lido.eth",
] as const;

// ─── GitHub ─────────────────────────────────────────────────────
export const POPULAR_GITHUB = [
  "octocat",
  "torvalds",
  "gaearon",
  "sindresorhus",
  "yyx990803",
  "tj",
  "mrdoob",
  "addyosmani",
] as const;

// ─── Country flags ──────────────────────────────────────────────
export const POPULAR_FLAGS = [
  "us", "gb", "ca", "de", "fr", "jp", "au", "br", "in", "kr",
  "cn", "mx", "it", "es", "nl", "se", "ch", "sg",
];

/** ISO country-code to display-name mapping (definitive). */
export const COUNTRY_CODES: Record<string, string> = {
  us: "United States",
  gb: "United Kingdom",
  ca: "Canada",
  de: "Germany",
  fr: "France",
  jp: "Japan",
  au: "Australia",
  br: "Brazil",
  in: "India",
  kr: "South Korea",
  cn: "China",
  mx: "Mexico",
  it: "Italy",
  es: "Spain",
  nl: "Netherlands",
  se: "Sweden",
  ch: "Switzerland",
  sg: "Singapore",
  ae: "UAE",
  za: "South Africa",
  ar: "Argentina",
  ng: "Nigeria",
  pt: "Portugal",
  ie: "Ireland",
} as const;

// ─── Style variants ─────────────────────────────────────────────
export const STYLE_DOMAINS = [
  "spotify.com", "github.com", "stripe.com", "netflix.com", "slack.com",
];

export const STYLE_VARIANTS = [
  { name: "Original", param: "" },
  { name: "Flat", param: "flat" },
  { name: "Monochrome", param: "monochrome" },
  { name: "Glossy", param: "glossy" },
  { name: "Pixel Art", param: "pixel-art" },
  { name: "Dot Grid", param: "dot-grid" },
  { name: "Vectorize", param: "vectorize" },
] as const;

// ─── Search playground config ───────────────────────────────────
export const SEARCH_CONFIG = {
  brand: {
    placeholder: "Search a brand, domain, or company...",
    hint: "Try: spotify, apple.com, figma, stripe",
  },
  ticker: {
    placeholder: "Search a stock ticker (AAPL, MSFT, TSLA)...",
    hint: "Try: AAPL, MSFT, TSLA, GOOGL, AMZN",
  },
  crypto: {
    placeholder: "Search a crypto token (btc, eth, sol)...",
    hint: "Try: btc, eth, sol, ada, doge",
  },
} as const;

export type SearchType = "brand" | "ticker" | "crypto";
